import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
    ArrowLeft, 
    Calendar, 
    FileAudio, 
    ExternalLink, 
    Upload,
    Loader2,
    Music,
    CheckCircle2,
    X,
    CloudUpload,
    Building2,
    DollarSign,
    Users,
    Clock,
    AlertTriangle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const MAX_FILE_SIZE = 500 * 1024 * 1024;

// Check if deadline has passed
const isDeadlinePassed = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
};

// Check if project is closed (either by status or deadline)
const isProjectClosed = (project) => {
    if (!project) return false;
    // Check if status is explicitly "closed"
    if (project.status === 'closed') return true;
    // Check if deadline has passed
    if (isDeadlinePassed(project.deadline)) return true;
    return false;
};

export default function PostedProjectDetailPage() {
    const { projectId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('');
    
    const [trackTitle, setTrackTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchProject();
    }, [projectId]);

    const fetchProject = async () => {
        try {
            const response = await fetch(`${API}/posted-projects/${projectId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProject(data);
                if (data.user_has_submitted) {
                    setSubmitted(true);
                }
            } else {
                toast.error('Project not found');
                navigate('/opportunities');
            }
        } catch (error) {
            toast.error('Error loading project');
        } finally {
            setLoading(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (file) => {
        const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav', 'audio/aiff', 'audio/x-aiff'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload an audio file (MP3, WAV, AIFF)');
            return;
        }
        
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
            return;
        }
        
        setAudioFile(file);
        if (!trackTitle) {
            setTrackTitle(file.name.replace(/\.[^/.]+$/, ''));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!audioFile) {
            toast.error('Please upload an audio file');
            return;
        }

        setSubmitting(true);
        setUploadProgress(0);
        
        try {
            // Step 1: Get presigned upload URL
            setUploadStatus('preparing');
            const presignedResponse = await fetch(`${API}/uploads/presigned`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sync_id: projectId,
                    file_name: audioFile.name,
                    content_type: audioFile.type,
                    file_size: audioFile.size
                })
            });

            const presignedData = await presignedResponse.json();
            
            if (!presignedResponse.ok) {
                throw new Error(presignedData.detail || 'Failed to prepare upload');
            }

            const { upload_url, submission_id, s3_key } = presignedData;
            setUploadProgress(10);
            
            // Step 2: Upload directly to S3
            setUploadStatus('uploading');
            
            const xhr = new XMLHttpRequest();
            
            await new Promise((resolve, reject) => {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = 10 + (event.loaded / event.total) * 80;
                        setUploadProgress(Math.round(percentComplete));
                    }
                });
                
                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error('Upload to storage failed'));
                    }
                });
                
                xhr.addEventListener('error', () => reject(new Error('Upload failed')));
                xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
                
                xhr.open('PUT', upload_url);
                xhr.setRequestHeader('Content-Type', audioFile.type);
                xhr.send(audioFile);
            });
            
            setUploadProgress(90);
            
            // Step 3: Confirm upload with backend
            setUploadStatus('confirming');
            const confirmResponse = await fetch(`${API}/uploads/confirm`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    submission_id,
                    sync_id: projectId,
                    s3_key,
                    track_title: trackTitle,
                    notes,
                    original_file_name: audioFile.name,
                    content_type: audioFile.type,
                    file_size: audioFile.size
                })
            });

            if (!confirmResponse.ok) {
                const error = await confirmResponse.json();
                throw new Error(error.detail || 'Failed to confirm upload');
            }

            setUploadProgress(100);
            setSubmitted(true);
            toast.success('Submission created successfully!');
            
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Error submitting track');
        } finally {
            setSubmitting(false);
            setUploadStatus('');
        }
    };

    const getUploadStatusText = () => {
        switch (uploadStatus) {
            case 'preparing':
                return 'Preparing upload...';
            case 'uploading':
                return `Uploading... ${uploadProgress}%`;
            case 'confirming':
                return 'Finalizing submission...';
            default:
                return 'Submit Track';
        }
    };

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto">
                {/* Back button */}
                <Link 
                    to="/opportunities" 
                    className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-6 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back to opportunities</span>
                </Link>

                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Project Details - Left */}
                    <div className="lg:col-span-3 space-y-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6"
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <Badge className={`
                                    ${project?.status === 'open' 
                                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                        : 'bg-[#404040] text-[#888] border-[#555]'
                                    }
                                `}>
                                    {project?.status}
                                </Badge>
                                <div className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                                    <Building2 className="w-4 h-4" />
                                    <span>{project?.poster_company || project?.poster_name}</span>
                                </div>
                            </div>

                            <h1 className="text-2xl font-bold text-white mb-4">{project?.title}</h1>
                            
                            <p className="text-[#A3A3A3] mb-6 whitespace-pre-wrap">{project?.description}</p>

                            {/* Genres */}
                            <div className="flex flex-wrap gap-2 mb-6">
                                {project?.genres?.map((genre, idx) => (
                                    <span 
                                        key={idx}
                                        className="px-3 py-1 bg-[#262626] rounded-full text-sm text-[#888]"
                                    >
                                        {genre}
                                    </span>
                                ))}
                            </div>

                            {/* Meta */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-[#333]">
                                <div className={`rounded-lg p-3 ${project && isProjectClosed(project) ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-[#262626]'}`}>
                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                        <Calendar className="w-3 h-3" />
                                        {project?.status === 'closed' ? 'Status' : 'Deadline'}
                                    </div>
                                    <p className={`font-medium text-sm ${project && isProjectClosed(project) ? 'text-orange-400' : 'text-white'}`}>
                                        {project?.status === 'closed' ? (
                                            <span>Project Closed</span>
                                        ) : (
                                            <>
                                                {formatDate(project?.deadline)}
                                                {project && isDeadlinePassed(project.deadline) && (
                                                    <span className="ml-2 text-xs text-orange-400">(Closed)</span>
                                                )}
                                            </>
                                        )}
                                    </p>
                                </div>
                                
                                <div className="bg-[#262626] rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                        <FileAudio className="w-3 h-3" />
                                        File Types
                                    </div>
                                    <p className="text-white font-medium text-sm">
                                        {project?.accepted_file_types?.join(', ')}
                                    </p>
                                </div>
                                
                                {project?.budget && (
                                    <div className="bg-[#262626] rounded-lg p-3">
                                        <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                            <DollarSign className="w-3 h-3" />
                                            Budget
                                        </div>
                                        <p className="text-green-400 font-medium text-sm">
                                            ${project.budget.toLocaleString()}
                                        </p>
                                    </div>
                                )}
                                
                                <div className="bg-[#262626] rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-[#888] text-xs mb-1">
                                        <Users className="w-3 h-3" />
                                        Submissions
                                    </div>
                                    <p className="text-white font-medium text-sm">
                                        {project?.submission_count || 0}
                                    </p>
                                </div>
                            </div>

                            {/* Notes */}
                            {project?.notes && (
                                <div className="mt-6 p-4 bg-[#262626] rounded-lg">
                                    <h3 className="text-sm font-medium text-white mb-2">Additional Notes</h3>
                                    <p className="text-sm text-[#A3A3A3]">{project.notes}</p>
                                </div>
                            )}

                            {/* Reference Links */}
                            {project?.reference_links?.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-lg font-bold text-white mb-4">Reference Tracks</h3>
                                    <div className="space-y-2">
                                        {project.reference_links.map((link, idx) => (
                                            <div
                                                key={idx}
                                                className="flex items-center gap-3 p-3 rounded-xl bg-[#262626] border border-[#404040]"
                                            >
                                                <ExternalLink className="w-4 h-4 text-[#666]" />
                                                <span className="flex-1 text-white text-sm truncate">
                                                    {link.text || link.href}
                                                </span>
                                                <a
                                                    href={link.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-3 py-1.5 text-xs bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-lg"
                                                >
                                                    Open
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>

                    {/* Submission Form - Right */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6 sticky top-6"
                        >
                            {/* Project Closed Banner */}
                            {project && isProjectClosed(project) && !submitted && (
                                <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-orange-400 font-medium">Submissions Closed</p>
                                        <p className="text-sm text-orange-300/80 mt-1">
                                            {project.status === 'closed' 
                                                ? 'This project has been closed by the poster. No new submissions are being accepted.'
                                                : 'The deadline for this project has passed. No new submissions are being accepted.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {submitted ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Submitted!</h3>
                                    <p className="text-[#A3A3A3] mb-6">
                                        Your track has been submitted. You'll be notified of any status updates.
                                    </p>
                                    <Link to="/submissions">
                                        <Button className="bg-[#F51E1E] hover:bg-[#D41212] text-white">
                                            View My Submissions
                                        </Button>
                                    </Link>
                                </div>
                            ) : project && isProjectClosed(project) ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                                        <Clock className="w-8 h-8 text-orange-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        {project.status === 'closed' ? 'Project Closed' : 'Deadline Passed'}
                                    </h3>
                                    <p className="text-[#A3A3A3] mb-6">
                                        This project is no longer accepting submissions. Check out other open opportunities.
                                    </p>
                                    <Link to="/opportunities">
                                        <Button className="bg-[#F51E1E] hover:bg-[#D41212] text-white">
                                            Browse Opportunities
                                        </Button>
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    <h2 className="text-xl font-bold text-white mb-6">Submit Your Track</h2>
                                    
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        {/* File Upload */}
                                        <div>
                                            <Label className="text-white mb-2 block">Audio File *</Label>
                                            <div
                                                onDragEnter={handleDrag}
                                                onDragLeave={handleDrag}
                                                onDragOver={handleDrag}
                                                onDrop={handleDrop}
                                                onClick={() => fileInputRef.current?.click()}
                                                className={`
                                                    border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
                                                    ${dragActive 
                                                        ? 'border-[#F51E1E] bg-[#F51E1E]/10' 
                                                        : audioFile 
                                                            ? 'border-green-500 bg-green-500/10' 
                                                            : 'border-[#404040] hover:border-[#666]'
                                                    }
                                                `}
                                            >
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".mp3,.wav,.aiff,audio/*"
                                                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                                                    className="hidden"
                                                />
                                                {audioFile ? (
                                                    <div className="flex items-center justify-center gap-3">
                                                        <Music className="w-8 h-8 text-green-400" />
                                                        <div className="text-left">
                                                            <p className="text-white font-medium">{audioFile.name}</p>
                                                            <p className="text-xs text-[#A3A3A3]">
                                                                {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setAudioFile(null);
                                                            }}
                                                            className="p-1 hover:bg-[#404040] rounded"
                                                        >
                                                            <X className="w-4 h-4 text-[#888]" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Upload className="w-10 h-10 text-[#666] mx-auto mb-3" />
                                                        <p className="text-[#A3A3A3]">
                                                            Drag & drop or click to upload
                                                        </p>
                                                        <p className="text-xs text-[#666] mt-1">
                                                            MP3, WAV, AIFF up to 500MB
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Track Title */}
                                        <div>
                                            <Label className="text-white mb-2 block">Track Title</Label>
                                            <Input
                                                value={trackTitle}
                                                onChange={(e) => setTrackTitle(e.target.value)}
                                                className="bg-[#262626] border-[#404040] text-white"
                                                placeholder="Enter track title"
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div>
                                            <Label className="text-white mb-2 block">Notes (optional)</Label>
                                            <Textarea
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                rows={3}
                                                className="bg-[#262626] border-[#404040] text-white resize-none"
                                                placeholder="Any additional info about your track..."
                                            />
                                        </div>

                                        {/* Submit */}
                                        <div className="space-y-3">
                                            {submitting && (
                                                <div className="space-y-2">
                                                    <Progress value={uploadProgress} className="h-2" />
                                                    <p className="text-xs text-center text-[#A3A3A3]">
                                                        {getUploadStatusText()}
                                                    </p>
                                                </div>
                                            )}
                                            <Button
                                                type="submit"
                                                disabled={submitting || !audioFile}
                                                className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full font-semibold"
                                            >
                                                {submitting ? (
                                                    <div className="flex items-center gap-2">
                                                        <Loader2 className="animate-spin" size={20} />
                                                        <span>{getUploadStatusText()}</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <CloudUpload size={20} />
                                                        <span>Submit Track</span>
                                                    </div>
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
